/**
 * Responsibility: put a kept card in sub-collections, and take it out again.
 *
 * The holding picker's twin, against the saved list rather than the
 * collection. They are kept as two small components rather than one taking a
 * "which kind" flag, because the only shared part is the chips and the only
 * differing part is which endpoint is called - a flag would hide that
 * difference at exactly the point somebody needs to see it.
 */
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { CollectionTag, LikedCard } from '../../api/client';
import { useSetLikeTags } from '../../api/hooks/useTags';

type Props = {
  card: LikedCard;
  tags: CollectionTag[];
};

export function LikedTagPicker({ card, tags }: Props) {
  const setTags = useSetLikeTags();
  const current = new Set(card.tags ?? []);

  if (tags.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No sub-collections yet. Make one to track what is still to buy for a run.
      </Typography>
    );
  }

  const toggle = (tagId: string) => {
    const next = new Set(current);
    if (next.has(tagId)) next.delete(tagId);
    else next.add(tagId);
    setTags.mutate({ productKey: card.product_key, tags: [...next] });
  };

  return (
    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        Sub-collections
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {tags.map((tag) => {
          const inIt = current.has(tag.id);
          return (
            <Chip
              key={tag.id}
              size="small"
              label={tag.name}
              color={inIt ? 'primary' : 'default'}
              variant={inIt ? 'filled' : 'outlined'}
              onClick={() => toggle(tag.id)}
              disabled={setTags.isPending}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
